import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, articles, siteInfo } from '@/mocks/siteData';

const categoryArticleImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20registration%20form%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20signup%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=600&height=350&seq=cat-article-1&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20login%20screen%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20authentication%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=600&height=350&seq=cat-article-2&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20cybersecurity%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20lock%20shield%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=600&height=350&seq=cat-article-3&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20announcement%20megaphone%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20update%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=600&height=350&seq=cat-article-4&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20football%20soccer%20stadium%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20sports%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=600&height=350&seq=cat-article-5&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20password%20reset%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20key%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=600&height=350&seq=cat-article-6&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.category === slug);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${siteInfo.url}/` },
              { '@type': 'ListItem', position: 2, name: currentCategory?.name || slug, item: `${siteInfo.url}/category/${slug}` },
            ],
          }),
        }}
      />

      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{currentCategory?.name || slug}</span>
          </nav>

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              {currentCategory?.name || '分类'}
            </h1>
            {currentCategory && (
              <p className="mt-2 text-foreground-600 text-sm md:text-base">
                {currentCategory.description}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:text-foreground-900 border border-background-200/60 hover:border-primary-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={categoryArticleImages[article.id]}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-semibold font-heading text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="mt-auto pt-4 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/50 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}